import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";
import { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Loader2, ArrowLeft } from "lucide-react";

export default function Login() {
  const { t } = useI18n();
  const { user, login, register } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("login");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", child_name: "" });

  useEffect(() => {
    if (user && user.role) nav(user.role === "admin" ? "/admin" : "/portal");
  }, [user, nav]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = mode === "login"
        ? await login(form.email, form.password)
        : await register({ name: form.name, email: form.email, password: form.password, child_name: form.child_name });
      toast.success(mode === "login" ? "Welcome back!" : "Account created!");
      nav(u.role === "admin" ? "/admin" : "/portal");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-navy flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-cream/70 hover:text-white mb-6 text-sm" data-testid="login-back-btn"><ArrowLeft className="h-4 w-4" /> Back to site</Link>
        <div className="bg-cream rounded-3xl p-8 shadow-2xl">
          <div className="flex items-center gap-2 mb-6">
            <div className="h-11 w-11 rounded-2xl bg-navy flex items-center justify-center text-cream font-heading font-bold text-lg">A</div>
            <div><div className="font-heading font-bold text-navy text-lg">Ainalem</div><div className="text-[10px] tracking-widest uppercase text-mustard-dark font-semibold">Parent Portal</div></div>
          </div>
          <h1 className="font-heading text-2xl font-bold text-navy mb-1">{mode === "login" ? t.auth.login : t.auth.register}</h1>
          <p className="text-sm text-navy/50 mb-6">Announcements, resources & secure payments.</p>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div className="space-y-1.5"><Label className="text-navy/80">{t.auth.name}</Label><Input required value={form.name} onChange={set("name")} data-testid="auth-name" /></div>
            )}
            <div className="space-y-1.5"><Label className="text-navy/80">{t.auth.email}</Label><Input type="email" required value={form.email} onChange={set("email")} data-testid="auth-email" /></div>
            <div className="space-y-1.5"><Label className="text-navy/80">{t.auth.password}</Label><Input type="password" required value={form.password} onChange={set("password")} data-testid="auth-password" /></div>
            {mode === "register" && (
              <div className="space-y-1.5"><Label className="text-navy/80">{t.auth.child}</Label><Input value={form.child_name} onChange={set("child_name")} data-testid="auth-child" /></div>
            )}
            <Button type="submit" disabled={loading} className="w-full rounded-full bg-coral hover:bg-coral-dark text-white h-11" data-testid="auth-submit-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (mode === "login" ? t.auth.loginBtn : t.auth.registerBtn)}
            </Button>
          </form>

          <div className="text-center mt-5 text-sm text-navy/60">
            {mode === "login" ? t.auth.noAccount : t.auth.haveAccount}{" "}
            <button onClick={() => setMode(mode === "login" ? "register" : "login")} className="text-coral font-semibold hover:underline" data-testid="auth-toggle-btn">
              {mode === "login" ? t.auth.register : t.auth.login}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
